/**
 * App Navigator — Static Configuration (React Navigation v8)
 *
 * Uses createStaticNavigation for automatic deep linking, type safety,
 * and the v8-recommended structure (tabs inside root stack).
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';

import * as SplashScreen from 'expo-splash-screen';
import {
  createStaticNavigation,
  createNavigationContainerRef,
} from '@react-navigation/native';
import { createNativeStackNavigator } from '@react-navigation/native-stack';
import { createBottomTabNavigator } from '@react-navigation/bottom-tabs';
import type { ColorValue } from 'react-native';
import Ionicons, { type IoniconsIconName } from '../components/ui/Ionicons';
import { useAuthStore } from '../store/authStore';
import { useUserPreferencesStore } from '../store/userPreferencesStore';
import { createLogger } from '../utils/logger';
import { selectOnboardingCompleted, selectFetchProfile } from '../store/userPreferencesSelectors';
import { useThemeColors, colors } from '../theme';
import { reactNavigationIntegration } from '../../index';
import { useCourseStore } from '../store/courseStore';
import { AppBanner } from '../components/ui/AppBanner';
import { CourseLoadErrorScreen } from '../screens/CourseLoadErrorScreen';

// ─── Screen imports ─────────────────────────────────────────────
import { LoginScreen } from '../screens/LoginScreen';
import { SignupScreen } from '../screens/auth/SignupScreen';
import { ForgotPasswordScreen } from '../screens/auth/ForgotPasswordScreen';
import { ResetPasswordScreen } from '../screens/auth/ResetPasswordScreen';
import { OnboardingScreen } from '../screens/OnboardingScreen';
import { ConversationHubScreen } from '../screens/ConversationHubScreen';
import { PracticeScreen } from '../screens/PracticeScreen';
import { ProfileScreen } from '../screens/ProfileScreen';
import { ChatScreen } from '../screens/ChatScreen';
import { ConversationListScreen } from '../screens/ConversationListScreen';
import { RolePlayCollectionsScreen } from '../screens/RolePlayCollectionsScreen';
import { RolePlayCollectionScreen } from '../screens/RolePlayCollectionScreen';
import { RolePlayDetailScreen } from '../screens/RolePlayDetailScreen';
import { RolePlayTemplatePreviewScreen } from '../screens/RolePlayTemplatePreviewScreen';
import { LessonDetailScreen } from '../screens/LessonDetailScreen';
import { SoundMapScreen } from '../screens/SoundMapScreen';
import { CompetenceMapScreen } from '../screens/CompetenceMapScreen';
import { VocabSetsScreen } from '../screens/VocabSetsScreen';
import { VocabSetDrillScreen } from '../screens/VocabSetDrillScreen';
import { AudioListenScreen } from '../screens/AudioListenScreen';
import { AudioSeriesScreen } from '../screens/AudioSeriesScreen';
import { VocabularyScreen } from '../screens/VocabularyScreen';
import { DueLessonsScreen } from '../screens/DueLessonsScreen';
import { AudioPlayerScreen } from '../screens/AudioPlayerScreen';
import { PaywallScreen } from '../screens/PaywallScreen';
import { LanguageSettingsScreen } from '../screens/LanguageSettingsScreen';
import { MicrophoneTestScreen } from '../screens/MicrophoneTestScreen';
import { useDisplayTheme } from '../hooks/useDisplayTheme';
import { AUDIO_LESSONS_ENABLED } from '../config/constants';

// ─── Auth hooks for conditional screens ─────────────────────────

function useIsAuthenticated(): boolean {
  return useAuthStore((s) => s.isAuthenticated);
}

function useIsNotAuthenticated(): boolean {
  return !useIsAuthenticated();
}

function useIsOnboarded(): boolean {
  const isAuthenticated = useIsAuthenticated();
  const onboardingCompleted = useUserPreferencesStore(selectOnboardingCompleted);
  return isAuthenticated && onboardingCompleted;
}

function useNeedsOnboarding(): boolean {
  const isAuthenticated = useIsAuthenticated();
  const onboardingCompleted = useUserPreferencesStore(selectOnboardingCompleted);
  return isAuthenticated && !onboardingCompleted;
}

// Audio Lessons is shelved behind a build-time flag (ADR-032). Static-API
// `if:` hooks gate the Listen tab and the audio stack screens; their
// auto-derived deep links (`listen`, `listen/...`) vanish with them.
function useAudioLessonsEnabled(): boolean {
  return AUDIO_LESSONS_ENABLED;
}

// ─── Tab icon helper ────────────────────────────────────────────

function tabIcon(
  ionicon: IoniconsIconName,
  ioniconFocused: IoniconsIconName,
) {
  return ({ color, size, focused }: { color: ColorValue; size: number; focused: boolean }) => (
    <Ionicons name={focused ? ioniconFocused : ionicon} size={size} color={color} />
  );
}

// ─── Tab Navigator (static) ─────────────────────────────────────

const MainTabs = createBottomTabNavigator({
  implementation: 'custom',
  screenOptions: ({ theme }) => ({
    headerShown: false,
    tabBarActiveTintColor: theme.dark ? colors.primary400 : colors.primary,
    tabBarInactiveTintColor: theme.dark ? colors.textSecondaryDark : colors.textSecondary,
    tabBarStyle: {
      backgroundColor: theme.colors.card,
      borderTopWidth: 1,
      borderTopColor: theme.colors.border,
    },
    tabBarLabelStyle: {
      fontSize: 12,
      fontWeight: '600' as const,
      fontFamily: theme.fonts.regular.fontFamily,
    },
  }),
  screens: {
    ConversationHub: {
      screen: ConversationHubScreen,
      linking: 'chat',
      options: {
        title: 'Chat',
        tabBarButtonTestID: 'tab-chat',
        tabBarIcon: tabIcon('chatbubble-ellipses-outline', 'chatbubble-ellipses'),
      },
    },
    Practice: {
      screen: PracticeScreen,
      linking: 'practice',
      options: {
        tabBarButtonTestID: 'tab-practice',
        tabBarIcon: tabIcon('book-outline', 'book'),
      },
    },
    Listen: {
      if: useAudioLessonsEnabled,
      screen: AudioListenScreen,
      linking: 'listen',
      options: {
        tabBarButtonTestID: 'tab-listen',
        tabBarIcon: tabIcon('headset-outline', 'headset'),
      },
    },
    Profile: {
      screen: ProfileScreen,
      linking: 'profile',
      options: {
        tabBarButtonTestID: 'tab-profile',
        tabBarIcon: tabIcon('person-circle-outline', 'person-circle'),
      },
    },
  },
});

// ─── Root Stack (static) ────────────────────────────────────────

export const RootStack = createNativeStackNavigator({
  screenOptions: {
    headerShown: false,
    contentStyle: { flex: 1 },
  },
  groups: {
    // Auth screens — shown when NOT authenticated
    Auth: {
      if: useIsNotAuthenticated,
      screenOptions: { headerShown: false },
      screens: {
        Login: {
          screen: LoginScreen,
          linking: 'login',
        },
        Signup: {
          screen: SignupScreen,
          linking: 'signup',
        },
        ForgotPassword: {
          screen: ForgotPasswordScreen,
          linking: 'forgot-password',
        },
        ResetPassword: {
          screen: ResetPasswordScreen,
          linking: 'reset-password/:token',
        },
      },
    },
    // Onboarding — shown when authenticated but not onboarded
    Onboarding: {
      if: useNeedsOnboarding,
      screenOptions: { headerShown: false },
      screens: {
        Onboarding: OnboardingScreen,
      },
    },
    // Main app — shown when fully authenticated and onboarded
    Main: {
      if: useIsOnboarded,
      screens: {
        MainTabs: {
          screen: MainTabs,
        },
        Chat: {
          screen: ChatScreen,
          linking: 'chat/:sessionId',
        },
        ConversationList: {
          screen: ConversationListScreen,
          linking: 'conversations',
        },
        // Phase 4: collection-grid catalog. The legacy `RolePlayList`
        // route name keeps pointing at the new collections screen so deep
        // links and existing navigators (ConversationHub) don't break.
        RolePlayList: {
          screen: RolePlayCollectionsScreen,
          linking: 'roleplay',
        },
        RolePlayCollection: {
          screen: RolePlayCollectionScreen,
          linking: 'roleplay/collection/:slug',
        },
        // Existing deep link kept as-is (UUID won't match the
        // collection/template prefixed routes above).
        RolePlayDetail: {
          screen: RolePlayDetailScreen,
          linking: 'roleplay/:slotId',
        },
        RolePlayTemplatePreview: {
          screen: RolePlayTemplatePreviewScreen,
          linking: 'roleplay/template/:templateId',
        },
        LessonDetail: {
          screen: LessonDetailScreen,
          linking: 'lesson/:slug',
        },
        SoundMap: {
          screen: SoundMapScreen,
          linking: 'sound-map',
        },
        // Grammar's counterpart of the Sound Map (entry: Practice hub card).
        CompetenceMap: {
          screen: CompetenceMapScreen,
          linking: 'competence-map',
        },
        // Themed word sets — the second route to a word alongside role play.
        VocabSets: {
          screen: VocabSetsScreen,
          linking: 'vocab-sets',
        },
        VocabSetDrill: {
          screen: VocabSetDrillScreen,
          linking: 'vocab-sets/:slug',
        },
        // "Your vocabulary" mastery-progress screen (entry: hub card).
        Vocabulary: {
          screen: VocabularyScreen,
          linking: 'vocabulary',
        },
        // Spaced-repetition review queue (entry: Practice hub card).
        DueLessons: {
          screen: DueLessonsScreen,
          linking: 'due-lessons',
        },
        // Audio Lessons drill-down (siblings of MainTabs). The bare `listen`
        // tab path lives on the Listen tab; these prefixed param routes are
        // more specific so they don't get shadowed by it.
        AudioSeries: {
          if: useAudioLessonsEnabled,
          screen: AudioSeriesScreen,
          linking: 'listen/series/:slug',
        },
        AudioPlayer: {
          if: useAudioLessonsEnabled,
          screen: AudioPlayerScreen,
          linking: 'listen/episode/:slug',
        },
LanguageSettings: {
          screen: LanguageSettingsScreen,
          linking: 'settings/language',
        },
        MicrophoneTest: {
          screen: MicrophoneTestScreen,
          linking: 'settings/microphone',
        },
        Paywall: {
          screen: PaywallScreen,
          options: { presentation: 'modal' as const },
          linking: 'pro',
        },
      },
    },
  },
});

// ─── Static Navigation component ────────────────────────────────

const Navigation = createStaticNavigation(RootStack);

// Navigation ref for use outside navigation context (e.g., ErrorBoundary)
export const navigationRef = createNavigationContainerRef();

const log = createLogger('AppNavigator');

/** Outcome of a `resetToInitialScreen()` call. */
export interface ResetNavResult {
  /** True when the navigator was reset. */
  reset: boolean;
  /** The route we reset to (present when `reset` is true). */
  route?: string;
  /** Why the reset was skipped (present when `reset` is false). */
  reason?: 'navigation-not-ready' | 'no-routes';
}

/**
 * Reset navigation to the initial screen of the CURRENT navigator state.
 * Used by the ErrorBoundary's retry and the `dev.reset-nav` E2E intent.
 *
 * No route is special-cased. The root navigator uses conditional `if:` groups
 * (Auth / Onboarding / Main) that already select the right group for the auth
 * state — Login when signed out, Onboarding when not onboarded, MainTabs (Hub)
 * when onboarded — so the "initial screen" is simply the first route of
 * whichever group is active (`routeNames[0]`). Resetting to a hard-coded
 * `MainTabs` broke whenever that group wasn't active ("RESET not handled by
 * any navigator"); resetting to the current root works in every auth state.
 *
 * Returns a result instead of throwing — the ErrorBoundary's retry calls this,
 * and throwing would break error recovery. Callers that need a hard failure
 * (the dev-intent handler) inspect the result.
 */
export function resetToInitialScreen(): ResetNavResult {
  if (!navigationRef.isReady()) {
    // Transient — the container hasn't mounted yet. Debug-level (no overlay).
    log.debug('resetToInitialScreen skipped — navigation container not ready');
    return { reset: false, reason: 'navigation-not-ready' };
  }
  const available = navigationRef.getRootState()?.routeNames ?? [];
  const initial = available[0];
  if (!initial) {
    // Genuine anomaly: ready but no routes. Loud in dev (log.error → red
    // LogBox overlay + Spotlight, easy to spot/screenshot), quiet breadcrumb
    // in prod (no Sentry issue).
    const msg = 'resetToInitialScreen — navigation ready but no routes available';
    if (__DEV__) log.error(msg, { availableRoutes: available });
    else log.warn(msg, { availableRoutes: available });
    return { reset: false, reason: 'no-routes' };
  }
  navigationRef.reset({ index: 0, routes: [{ name: initial }] });
  return { reset: true, route: initial };
}

// ─── App Navigator ──────────────────────────────────────────────

export function AppNavigator() {
  const { isLoading } = useAuthStore();
  const isAuthenticated = useIsAuthenticated();
  const fetchProfile = useUserPreferencesStore(selectFetchProfile);
  const [prefsFetched, setPrefsFetched] = useState(false);

  // Theme for React Navigation (powers screenOptions({ theme }) callbacks)
  const themeColors = useThemeColors();
  const { fontFamily } = useDisplayTheme();

  const navTheme = useMemo(() => {
    const family = fontFamily || 'System';
    return {
      dark: themeColors.isDark,
      colors: {
        primary: colors.primary,
        background: themeColors.background,
        card: themeColors.card,
        text: themeColors.text,
        border: themeColors.border,
        notification: colors.error,
      },
      fonts: {
        regular: { fontFamily: family, fontWeight: '400' as const },
        medium: { fontFamily: family, fontWeight: '500' as const },
        bold: { fontFamily: family, fontWeight: '700' as const },
        heavy: { fontFamily: family, fontWeight: '900' as const },
      },
    };
  }, [themeColors, fontFamily]);

  // Fetch data for UI when authenticated. Services (queue, socket, RevenueCat)
  // are started by onAuthenticated in auth/store.ts — not here.
  //
  // `courseStore.load()` no longer throws on first-install failure — it
  // sets `loadError` instead (per DCOURSE-007). The promise always
  // resolves, `prefsFetched` flips to true, the splash drops, and the
  // gate below picks the right surface: navigation tree if loaded,
  // `CourseLoadErrorScreen` otherwise.
  //
  // `fetchProfile().catch(() => {})` is non-blocking — cached
  // preferences (or defaults) are enough to start.
  //
  // Intentional: setState in async callbacks syncs auth state with profile fetch
  /* eslint-disable react-hooks/set-state-in-effect */
  useEffect(() => {
    if (isAuthenticated) {
      Promise.all([
        useCourseStore.getState().load(),
        fetchProfile().catch(() => {}),
      ]).then(() => setPrefsFetched(true));
    } else {
      setPrefsFetched(false);
    }
  }, [isAuthenticated, fetchProfile]);
  /* eslint-enable react-hooks/set-state-in-effect */

  // Boot-gate selectors — individual subscriptions (CHAT_PERFORMANCE.md
  // rule "Zustand selectors must be individual"). The pair drives the
  // DCOURSE-007 retry-screen decision below. Reading these here (not
  // inside the conditional) so the navigator re-renders on retry-success
  // and naturally unmounts the error screen when `loaded` flips true.
  const coursesLoaded = useCourseStore((s) => s.loaded);
  const coursesLoadError = useCourseStore((s) => s.loadError);

  // Hide splash screen once app is ready to render
  const isReady = !isLoading && (!isAuthenticated || prefsFetched);
  const onLayoutReady = useCallback(() => {
    if (isReady) {
      SplashScreen.hideAsync().catch(() => {});
    }
  }, [isReady]);

  if (!isReady) {
    return null;
  }

  // DCOURSE-007 boot gate: fresh-install course load failed with no
  // cache to fall back on. Render the retry screen as the WHOLE surface
  // (no navigation tree, no tabs, no chat) until retry succeeds. Drop
  // the splash via the same `onLayoutReady` path so the screen actually
  // shows up — without this, the splash stays over the error screen
  // until the user taps retry, which defeats the point.
  if (isAuthenticated && coursesLoadError !== null && !coursesLoaded) {
    SplashScreen.hideAsync().catch(() => {});
    return (
      <>
        <CourseLoadErrorScreen />
        <AppBanner />
      </>
    );
  }

  return (
    <>
      <Navigation
        ref={navigationRef}
        linking={{ enabled: 'auto' }}
        theme={navTheme}
        onReady={() => {
          onLayoutReady();
          if (reactNavigationIntegration) {
            reactNavigationIntegration.registerNavigationContainer(navigationRef);
          }
        }}
      />
      <AppBanner />
    </>
  );
}
